import { Component, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { ImageService } from 'src/app/Core/services/image.service';
import { ToasterService } from 'src/app/Core/services/toaster.service';

@Component({
  selector: 'app-photo-upload',
  templateUrl: './photo-upload.component.html',
  styleUrls: ['./photo-upload.component.css']
})
export class PhotoUploadComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  showProgress: boolean = false;
  progress: number = 0;
  showUrlProgress: boolean = false;
  urlProgress: number = 0;
  fileName: string = '';
  uploadedImage: string | ArrayBuffer | null = null;
  imageName: string = '';
  imageSize: number = 0;
  urlError: string = '';


  private isUploadCanceled: boolean = false;

  constructor(private router: Router, private imageService: ImageService, private toaster: ToasterService) {}

  photoUpload(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.fileInput.nativeElement.click();
  }

  triggerFileInput(event: Event) {
    event.stopPropagation();
    this.fileInput.nativeElement.click();
  }

  async onFileDrop(event: DragEvent) {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files && files.length) {
      const file = files[0];
      const isValidImage = await this.validateImage(file);
      if (isValidImage) {
        this.fileName = file.name;
        this.previewFile(file);
        this.uploadFile(file);
      } else {
        this.toaster.openDialogBox('Invalid file type. Please upload a .jpeg, .jpg, or .png file.', false);
      }
    }
  }

  onFileOver(event: DragEvent) {
    event.preventDefault();
  }

  async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      const file = input.files[0];
      this.fileName = file.name;
      const isValidImage = await this.validateImage(file);
      if (isValidImage) {
        this.previewFile(file);
        this.uploadFile(file);
      } else {
        this.toaster.openDialogBox('Invalid file type. Please upload a .jpeg, .jpg, or .png file.', false);
      }
    }
    input.value = '';
  }

  validateImage(file: Blob): Promise<boolean> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    });
  }

  previewFile(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      this.uploadedImage = reader.result;
    };
    reader.readAsDataURL(file);
  }

  uploadFile(file: File) {
    this.showProgress = true;
    this.isUploadCanceled = false;
    const uploadInterval = setInterval(() => {
      if (this.isUploadCanceled) {
        clearInterval(uploadInterval);
        this.resetUploadState();
        return;
      }

      this.progress += 10;
      if (this.progress >= 100) {
        clearInterval(uploadInterval);
        if (!this.isUploadCanceled) {
          this.showProgress = false;
          this.imageService.setImage(this.uploadedImage);
          this.router.navigate(['/image-preview']);
        }
      }
    }, 500);
  }

  async uploadFromUrl(url: string) {
    if (!this.isValidImageUrl(url)) {
      this.urlError = 'Invalid URL. Please enter a valid image URL ending with .jpeg, .jpg, or .png.';
      return;
    }
  
    this.urlError = '';
    this.showUrlProgress = true;
    this.urlProgress = 0;
    this.isUploadCanceled = false;
  
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Invalid image URL');
      }
      const blob = await response.blob();

      const isValidImage = await this.validateImage(blob);
      if (!isValidImage) {
        this.urlError = 'Invalid URL. Please enter a valid image URL ending with .jpeg, .jpg, or .png.';
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        this.uploadedImage = reader.result as string;
      };
      reader.readAsDataURL(blob);
  
      const urlUploadInterval = setInterval(() => {
        if (this.isUploadCanceled) {
          clearInterval(urlUploadInterval);
          this.resetUploadState();
          return;
        }
  
        this.urlProgress += 10;
        if (this.urlProgress >= 100) {
          clearInterval(urlUploadInterval);
          this.showUrlProgress = false;
          
          // Ensuring that the image is set before navigating
          if (this.uploadedImage) {
            this.imageService.setImage(this.uploadedImage);
            this.router.navigate(['/image-preview']);
          }
        }
      }, 500); // Adjust the interval as needed
    } catch (error) {
      console.error('Failed to fetch image metadata', error);
      this.urlError = 'Failed to upload image from URL. Please ensure the URL is correct.';
      this.resetUploadState();
    }
  }
  
  isValidImageUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      const validExtensions = ['.jpeg', '.jpg', '.png'];
      return validExtensions.some(ext => parsedUrl.pathname.endsWith(ext));
    } catch {
      return false;
    }
  }

  abortUpload() {
    this.isUploadCanceled = true;
  }

  resetUploadState() {
    this.showProgress = false;
    this.progress = 0;
    this.showUrlProgress = false;
    this.urlProgress = 0;
    this.uploadedImage = null;
    this.fileName = '';
    this.imageName = '';
    this.imageSize = 0;
  }

  navigate() {
    this.router.navigate(['/upload']);
  }
}
